/* eslint-disable jsx-a11y/media-has-caption */
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Collapse, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { black, purple, transparentPurple, universalStyles } from '../../../../app/theme';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import ExternalLinkModal from '../../../micro/ExternalLinkModal/ExternalLinkModal';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  header: {
    margin: '30px 0 0',
    fontSize: 22,
  },
  findings: {
    margin: '40px 0 0 0',
  },
  findingsSub: {
    fontStyle: 'italic',
    fontWeight: 'normal',
  },
  purpleBox: {
    width: '100%',
    margin: '26px 26px 0 -26px',
    backgroundColor: transparentPurple,
    padding: 26,
  },
  titleText: {
    color: black,
    fontSize: 22,
    fontWeight: 'bold',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    '&::before': {
      content: '"+"',
      width: '1em',
      display: 'inline-block',
      color: purple,
      fontWeight: 600,
    },
  },
  expanded: {
    '&::before': {
      content: '"-"',
    },
  },
  collapseContents: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    textAlign: 'center',
  },
  image: {
    maxHeight: 300,
    width: 'auto',
  },
  fullImage: {
    width: '80%',
    marginLeft: '10%',
  },
  smallImage: {
    width: '80%',
  },
  link: {
    textTransform: 'none',
    padding: 0,
    fontSize: 'inherit',
    fontStyle: 'italic',
    color: 'inherit',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  caption: {
    margin: 8,
  },
  text: {
    margin: '40px auto 15px',
    textAlign: 'center',
  },
}));

type InterpretationPage18Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const InterpretationPage18 = ({ setNextDisabled }: InterpretationPage18Props): JSX.Element => {
  const classes = useStyles();
  const [isExpanded1, setIsExpanded1] = useState(false);
  const [isExpanded2, setIsExpanded2] = useState(false);
  const [isExpanded3, setIsExpanded3] = useState(false);
  const [isExpanded4, setIsExpanded4] = useState(false);
  const [isExpanded5, setIsExpanded5] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_4.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_4.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 4:</Typography>
          <Typography variant="h3">Exclude Physiological Variants and Incidental Findings</Typography>
          <Typography variant="h5" className={classes.header}>
            Introduction
          </Typography>
          <ul className={classes.list}>
            <li>
              In order to interpret images, the physician should be aware of potential for Axumin injection uptake in
              areas of benign pathology and other incidental cancers
            </li>
            <li>This section will:</li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>
                Describe physiological variants and incidental findings noted following use of Axumin in patients with
                various conditions
              </li>
              <li>
                Provide examples resulting from nonspecific uptake of this amino acid tracer in areas of inflammation
                and in other benign and malignant pathologies
              </li>
              <li>
                Represent potential incidental findings with Axumin injection outside of its indicated use for positron
                emission tomography (PET) imaging in men with suspected prostate cancer recurrence
              </li>
            </ul>
          </ul>
          <Typography variant="h5" className={classes.header}>
            False Positive
          </Typography>
          <ul className={classes.list}>
            <li>
              Axumin uptake is not specific for prostate cancer and may occur with other types of cancer, prostatitis
              and benign prostatic hyperplasia
            </li>
            <li>
              False positive cases have also been described in association with an inflammatory response after
              cryotherapy and radiation artifacts in patients previously treated with radiotherapy
            </li>
            <li>
              Clinical correlation, which may include histopathological evaluation of the suspected recurrence site,
              should be considered where appropriate
            </li>
            <li>PSA levels seem to have an impact on the detection rate of Axumin</li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>In general, patients with negative scans had lower PSA levels than those with positive scans</li>
            </ul>
            <li>
              Physicians should be aware of potential for Axumin uptake in areas of benign pathology and other
              incidental cancers; these potential incidental findings with Axumin are outside of its indicated use for
              PET imaging in men with suspected prostate cancer recurrence
            </li>
          </ul>
          <Typography className={classes.findings} variant="h5">
            Incidental Uptake and Variants
          </Typography>
          <Typography className={classes.findingsSub} variant="h5">
            To review the specific incidental findings and variants, select “+” below
          </Typography>
          <div className={classes.purpleBox}>
            <Typography
              className={classNames(classes.titleText, isExpanded1 ? classes.expanded : '')}
              onClick={() => setIsExpanded1(!isExpanded1)}
            >
              Osteoid Osteoma
            </Typography>
            <Collapse in={isExpanded1} timeout="auto" unmountOnExit>
              <div className={classes.collapseContents}>
                <img
                  className={classes.image}
                  src={`${BASE_URL}${IMAGE_PATHS.OSTEIODOSTEOMA.path}`}
                  alt={IMAGE_PATHS.OSTEIODOSTEOMA.alt}
                />
              </div>
            </Collapse>
          </div>
          <div className={classes.purpleBox}>
            <Typography
              className={classNames(classes.titleText, isExpanded2 ? classes.expanded : '')}
              onClick={() => setIsExpanded2(!isExpanded2)}
            >
              Ringworm
            </Typography>
            <Collapse in={isExpanded2} timeout="auto" unmountOnExit>
              <Typography variant="body1" className={classes.text}>
                Mild symmetric inguinal nodal uptake is commonly present. It may be more intense with inflammation, as
                in this example
              </Typography>
              <div className={classes.collapseContents}>
                <img
                  className={classes.image}
                  src={`${BASE_URL}${IMAGE_PATHS.RINGWORM.path}`}
                  alt={IMAGE_PATHS.RINGWORM.alt}
                />
              </div>
            </Collapse>
          </div>
          <div className={classes.purpleBox}>
            <Typography
              className={classNames(classes.titleText, isExpanded3 ? classes.expanded : '')}
              onClick={() => setIsExpanded3(!isExpanded3)}
            >
              Meningioma
            </Typography>
            <Collapse in={isExpanded3} timeout="auto" unmountOnExit>
              <div className={classes.collapseContents}>
                <img
                  className={classes.image}
                  src={`${BASE_URL}${IMAGE_PATHS.MENINGIOMA.path}`}
                  alt={IMAGE_PATHS.MENINGIOMA.alt}
                />
              </div>
            </Collapse>
          </div>
          <div className={classes.purpleBox}>
            <Typography
              className={classNames(classes.titleText, isExpanded4 ? classes.expanded : '')}
              onClick={() => setIsExpanded4(!isExpanded4)}
            >
              Benign Prostatic Hyperplasia and Prostatitis
            </Typography>
            <Collapse in={isExpanded4} timeout="auto" unmountOnExit>
              <Typography variant="body1" className={classes.text}>
                Axumin uptake in areas of benign prostate pathology may be indistinguishable from uptake in areas of
                prostate cancer
              </Typography>
              <div className={classes.collapseContents}>
                <div>
                  <img
                    className={classes.smallImage}
                    src={`${BASE_URL}${IMAGE_PATHS.BENIGN_PROSTATIC.path}`}
                    alt={IMAGE_PATHS.BENIGN_PROSTATIC.alt}
                  />
                  <Typography variant="body1" className={classes.caption}>
                    <b>Benign prostatic hyperplasia</b>
                  </Typography>
                </div>
                <div>
                  <img
                    className={classes.smallImage}
                    src={`${BASE_URL}${IMAGE_PATHS.PROSTATITIS.path}`}
                    alt={IMAGE_PATHS.PROSTATITIS.alt}
                  />
                  <Typography variant="body1" className={classes.caption}>
                    <b>Prostatitis</b>
                  </Typography>
                </div>
              </div>
            </Collapse>
          </div>
          <div className={classes.purpleBox}>
            <Typography
              className={classNames(classes.titleText, isExpanded5 ? classes.expanded : '')}
              onClick={() => setIsExpanded5(!isExpanded5)}
            >
              Tubulovillous Adenoma With Atypia
            </Typography>
            <Collapse in={isExpanded5} timeout="auto" unmountOnExit>
              <div className={classes.collapseContents}>
                <div>
                  <Typography variant="body1" className={classes.text}>
                    Incidental non-physiologic uptake can occur
                  </Typography>
                  <img
                    className={classes.image}
                    src={`${BASE_URL}${IMAGE_PATHS.TUBULOVILLOUSADENOMA.path}`}
                    alt={IMAGE_PATHS.TUBULOVILLOUSADENOMA.alt}
                  />
                </div>
              </div>
            </Collapse>
          </div>
          <Typography className={classes.findings} variant="h5">
            For additional information on uptake patterns and incidental findings, please review the article
          </Typography>
          <Typography className={classes.findingsSub} variant="h5">
            https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4844004/
          </Typography>
          <img
            className={classes.fullImage}
            src={`${BASE_URL}${IMAGE_PATHS.ARTICLE_CLIP.path}`}
            alt={IMAGE_PATHS.ARTICLE_CLIP.alt}
          />
        </div>
      </div>
      <LearningCheckQuestions
        module="axuminInterpretation"
        page={18}
        setNextDisabled={setNextDisabled}
        trainingId={1}
      />
      <ExternalLinkModal
        url="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4844004/"
        isOpen={isModalOpen}
        handleModalOpen={(open: boolean) => setIsModalOpen(open)}
      />
    </>
  );
};

export default InterpretationPage18;
